import { AxiosResponse } from 'axios';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { LanguageKeys } from 'src/common/constants/languages';
import { IMergeCandidate } from 'src/interfaces/MergeCandidate';
import { IPendingClaim } from 'src/interfaces/PendingClaim';
import { IEnrichedParticipant, IFormParticipant, IParticipant } from 'src/interfaces/Participant';

export type FetchParticipantsOptions = {
  editionId?: string;
  ownerUserId?: string;
  /** When true with editionId, include participants without a registration/check-in (check-in wizard). */
  includeUnregistered?: boolean;
};

export type MergeParticipantsResult = {
  survivor: IParticipant;
  absorbedIds: string[];
  affectedEditionIds: string[];
};

function participantWritePayload(contestant: IFormParticipant) {
  const payload: Record<string, unknown> = {
    ...contestant,
    dateOfBirth: contestant.dateOfBirth
      ? (contestant.dateOfBirth instanceof Date
        ? contestant.dateOfBirth.toISOString()
        : contestant.dateOfBirth)
      : undefined,
  };
  delete payload.age;
  return payload;
}

export interface IParticipantsState {
  fetchParticipants: (
    options?: string | FetchParticipantsOptions,
  ) => Promise<void | AxiosResponse<null>>;
  listParticipantsForOwner: (
    editionId: string,
    ownerUserId: string,
  ) => Promise<IEnrichedParticipant[]>;
  /** Edition participants with no owner (check-in wizard find step). */
  listUnownedParticipantsForEdition: (editionId: string) => Promise<IEnrichedParticipant[]>;
  fetchMyParticipants: () => Promise<void | AxiosResponse<null>>;
  deleteParticipant: (id: string) => Promise<void | AxiosResponse<null>>;
  deleteAllParticipants: (
    refreshOptions?: FetchParticipantsOptions,
  ) => Promise<void | AxiosResponse<null>>;
  participants: IParticipant[];
  mergeCandidates: IMergeCandidate[];
  pendingClaims: IPendingClaim[];
  isLoadingParticipants: boolean;
  isLoadingMergeCandidates: boolean;
  isMergingParticipants: boolean;
  isLoadingPendingClaims: boolean;
  fetchMyPendingClaims: () => Promise<void | AxiosResponse<null>>;
  fetchMergeCandidates: () => Promise<void>;
  mergeParticipants: (participantIds: string[]) => Promise<MergeParticipantsResult>;
  abandonClaim: (claimId: string) => Promise<void>;
  abandonClaimParticipant: (claimId: string, participantId: string) => Promise<void>;
  resetParticipants: () => void;
  saveParticipant: (
    contestant: IFormParticipant,
    id?: string,
    lang?: LanguageKeys,
  ) => Promise<void | AxiosResponse<IParticipant[]>>;
  saveParticipantAsAdmin: (
    contestant: IFormParticipant,
    id?: string,
  ) => Promise<void | AxiosResponse<IParticipant>>;
  initiateClaim: (
    email: string,
    lastName: string,
    lang?: LanguageKeys,
  ) => Promise<{ pending: number; emailsSent?: number }>;
  confirmClaim: (token: string) => Promise<{ claimed: boolean; count: number; participantIds?: string[] }>;
}

const useParticipantsStore = create<IParticipantsState>(set => {
  const buildParticipantsUrl = (options?: string | FetchParticipantsOptions) => {
    const resolved: FetchParticipantsOptions =
      typeof options === 'string' ? { editionId: options } : options ?? {};
    const params = new URLSearchParams();
    if (resolved.editionId) {
      params.set('editionId', resolved.editionId);
    }
    if (resolved.ownerUserId) {
      params.set('ownerUserId', resolved.ownerUserId);
    }
    if (resolved.includeUnregistered) {
      params.set('includeUnregistered', 'true');
    }
    const query = params.toString();
    return query ? `/participants?${query}` : '/participants/';
  };

  const getParticipants = async (options?: string | FetchParticipantsOptions) => {
    const url = buildParticipantsUrl(options);
    return SECURE_API.get<IParticipant[]>(url)
      .then(({ data }) => set({ participants: data, isLoadingParticipants: false }))
      .catch((error: unknown) => {
        set({ participants: [], isLoadingParticipants: false });
        console.log({ error });
      });
  };

  const getMyParticipants = async () => {
    return SECURE_API.get<IParticipant[]>('/me/participants/')
      .then(({ data }) => set({ participants: data, isLoadingParticipants: false }))
      .catch((error: unknown) => {
        set({ participants: [], isLoadingParticipants: false });
        console.log({ error });
      });
  };

  const getMyPendingClaims = async () => {
    return SECURE_API.get<IPendingClaim[]>('/me/participants/claims')
      .then(({ data }) => set({ pendingClaims: data, isLoadingPendingClaims: false }))
      .catch((error: unknown) => {
        set({ pendingClaims: [], isLoadingPendingClaims: false });
        console.log({ error });
      });
  };

  const getMergeCandidates = async () => {
    return SECURE_API.get<IMergeCandidate[]>('/participants/merge-candidates')
      .then(({ data }) => set({ mergeCandidates: data, isLoadingMergeCandidates: false }))
      .catch((error: unknown) => {
        set({ mergeCandidates: [], isLoadingMergeCandidates: false });
        console.log({ error });
        throw error;
      });
  };

  return {
    participants: [],
    mergeCandidates: [],
    pendingClaims: [],
    isLoadingParticipants: false,
    isLoadingMergeCandidates: false,
    isMergingParticipants: false,
    isLoadingPendingClaims: false,
    fetchMyPendingClaims: async () => {
      set({ isLoadingPendingClaims: true });
      return getMyPendingClaims();
    },
    fetchMergeCandidates: async () => {
      set({ isLoadingMergeCandidates: true });
      await getMergeCandidates();
    },
    mergeParticipants: async (participantIds: string[]) => {
      set({ isMergingParticipants: true });
      try {
        const { data } = await SECURE_API.post<MergeParticipantsResult>(
          '/participants/merge',
          { participantIds },
        );
        await getMergeCandidates();
        return data;
      } finally {
        set({ isMergingParticipants: false });
      }
    },
    abandonClaim: async (claimId: string) => {
      await SECURE_API.delete(`/me/participants/claims/${claimId}`);
      await getMyPendingClaims();
    },
    abandonClaimParticipant: async (claimId: string, participantId: string) => {
      await SECURE_API.delete(`/me/participants/claims/${claimId}/participants/${participantId}`);
      await getMyPendingClaims();
    },
    fetchParticipants: async (options?: string | FetchParticipantsOptions) => {
      set({ isLoadingParticipants: true });
      return getParticipants(options);
    },
    listParticipantsForOwner: async (editionId: string, ownerUserId: string) => {
      const { data } = await SECURE_API.get<IEnrichedParticipant[]>(
        buildParticipantsUrl({ editionId, ownerUserId, includeUnregistered: true }),
      );
      return data;
    },
    listUnownedParticipantsForEdition: async (editionId: string) => {
      const { data } = await SECURE_API.get<IEnrichedParticipant[]>(
        buildParticipantsUrl({ editionId, includeUnregistered: true }),
      );
      return data.filter(p => p.ownerUserId == null || p.ownerUserId === '');
    },
    fetchMyParticipants: async () => {
      set({ isLoadingParticipants: true });
      return getMyParticipants();
    },
    saveParticipant: async (contestant: IFormParticipant, id?: string, lang?: LanguageKeys) => {
      set({ isLoadingParticipants: true });
      const payload = participantWritePayload(contestant);
      const saveParticipantPromise = id
        ? SECURE_API.patch<IParticipant[]>(`/me/participants/${id}`, payload)
        : SECURE_API.post<IParticipant[]>(`/me/participants${lang ? '?lang=' + lang : ''}`, payload);

      return saveParticipantPromise.finally(() => {
        getMyParticipants();
      });
    },
    saveParticipantAsAdmin: async (contestant: IFormParticipant, id?: string) => {
      set({ isLoadingParticipants: true });
      const payload = participantWritePayload(contestant);
      delete (payload as Record<string, unknown>).ownerUserId;
      const saveParticipantPromise = id
        ? SECURE_API.patch<IParticipant>(`/participants/${id}`, payload)
        : SECURE_API.post<IParticipant>(`/participants`, payload);

      // Caller (System Participants) refetches with edition/owner filters —
      // do not call getParticipants() here or an unfiltered list will race.
      return saveParticipantPromise.finally(() => {
        set({ isLoadingParticipants: false });
      });
    },
    deleteParticipant: async id => {
      set({ isLoadingParticipants: true });
      return SECURE_API.delete<null>(`/me/participants/${id}`).finally(() => {
        getMyParticipants();
      });
    },
    deleteAllParticipants: async (refreshOptions?: FetchParticipantsOptions) => {
      set({ isLoadingParticipants: true });
      return SECURE_API.delete<null>(`/participants/bulk`).finally(() => {
        getParticipants(refreshOptions);
      });
    },
    resetParticipants: () => set({ participants: [], mergeCandidates: [] }),
    initiateClaim: async (email: string, lastName: string, lang?: LanguageKeys) => {
      const { data } = await SECURE_API.post<{ pending: number; emailsSent?: number }>(
        '/me/participants/claim',
        { email, lastName, lang },
      );
      await getMyPendingClaims();
      return data;
    },
    confirmClaim: async (token: string) => {
      const { data } = await SECURE_API.post<{ claimed: boolean; count: number; participantIds?: string[] }>(
        '/me/participants/claim/confirm',
        { token },
      );
      await Promise.all([getMyParticipants(), getMyPendingClaims()]);
      return data;
    },
  };
});

export default useParticipantsStore;
